import React, { useState, useEffect } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import styles from './ResultWorliMatka.module.css';

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
    if (!cardCode) return null;

    try {
        if (cardCode.length < 3) {
            return null;
        }

        // Use cards folder with full card code (e.g., "2DD.png", "ASS.png")
        // Format: rank + suit (DD, SS, CC, HH)
        // In admin panel, cards are usually in /assets/img/cards/
        return `/assets/img/cards/${cardCode}.png`;
    } catch (e) {
        return null;
    }
};

// Helper function to parse card string (format: "7CC,3CC,9SS")
const parseCards = (cardString) => {
    if (!cardString) return [];

    const cards = cardString.split(',');
    return cards.map(card => {
        const trimmed = card.trim();
        return trimmed ? getCardImage(trimmed) : null;
    }).filter(Boolean);
};

// Helper function to parse rdesc for worli2
const parseRdesc = (rdesc) => {
    if (!rdesc) return {};

    const parts = rdesc.split('#');
    const pana = parts[0] || "";
    const ocada = parts[1] || "";

    return {
        pana,
        ocada,
    };
};

const ResultWorliMatka = ({
    data = [],
    gameData = {},
    gameType = "worli2" // Default to worli2 (Worli Matka), but can be "worli"
}) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [lastResults, setLastResults] = useState([]);
    const [detailResult, setDetailResult] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch last results on mount
    useEffect(() => {
        const fetchLastResults = async () => {
            try {
                const response = await getLastResults(gameType);
                if (response?.success && response?.data?.data?.res) {
                    setLastResults(response.data.data.res);
                }
            } catch (error) {
                console.error('Error fetching last results:', error);
            }
        };

        fetchLastResults();
        const interval = setInterval(fetchLastResults, 5000);
        return () => clearInterval(interval);
    }, [gameType]);

    const handleResultClick = async (resultItem, index) => {
        setSelectedResult(resultItem);
        setLoading(true);

        const mid = resultItem?.mid;

        if (mid) {
            try {
                const response = await getDetailResults(gameType, mid.toString());
                if (response?.success && response?.data?.data?.t1) {
                    setDetailResult(response.data.data.t1);
                }
            } catch (error) {
                console.error('Error fetching detail results:', error);
            }
        }

        setLoading(false);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedResult(null);
        setDetailResult(null);
    };

    // Format modal data based on selected result
    const getModalData = () => {
        if (!selectedResult || !detailResult) return null;

        const cards = parseCards(detailResult.card);
        const rdescData = parseRdesc(detailResult.rdesc);

        return {
            roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
            matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
            cards: cards,
            resultDetails: {
                pana: rdescData.pana || "",
                ocada: rdescData.ocada || "",
            },
        };
    };

    const modalData = getModalData();

    // Combine API results with fallback data
    const allResults = lastResults.length > 0
        ? lastResults
        : data.map((res, idx) => ({
            win: res || "",
            mid: null
        }));

    return (
        <>
            <div className={styles.resultRow}>
                {allResults.map((resultItem, index) => {
                    const win = resultItem.win || resultItem;

                    return (
                        <div
                            key={index}
                            className={styles.resultCircle}
                            onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
                            style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
                        >
                            R
                        </div>
                    );
                })}
            </div>

            {/* Modal */}
            {modalData && (
                <ResultModalLayout
                    show={showModal}
                    onHide={handleCloseModal}
                    title={gameType === "worli2" ? "Worli Matka Result" : "Worli Result"}
                    roundId={modalData.roundId}
                    matchTime={modalData.matchTime}
                    loading={loading}
                >
                    <div className="d-flex justify-content-center">
                        <div className="player-number">
                            <div className="d-flex justify-content-center">
                                {modalData.cards && modalData.cards.length > 0 && modalData.cards.map((cardImage, index) => (
                                    <img
                                        key={index}
                                        src={cardImage}
                                        alt={`Card ${index + 1}`}
                                        className={styles.resultCard}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                ))}
                            </div>
                            <div className="mt-2 text-white text-center">
                                {modalData.resultDetails.pana && (
                                    <p className="mb-1">
                                        <b>Pana:</b> {modalData.resultDetails.pana}
                                    </p>
                                )}
                                {modalData.resultDetails.ocada && (
                                    <p className="mb-0">
                                        <b>Ocada:</b> {modalData.resultDetails.ocada}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </ResultModalLayout>
            )}
        </>
    );
};

export default ResultWorliMatka;
